import { type Locale, t } from "./i18n";
import { type OfficialRoute } from "./site";

export const officialSiteOrigin = "https://collectie.io";
export const officialSocialImage = `${officialSiteOrigin}/collectie-social.png`;

const routeMetadataKeys: Record<
  OfficialRoute,
  { title: string; description: string; path: string | null }
> = {
  home: {
    title: "official.meta.title",
    description: "official.meta.description",
    path: "/",
  },
  privacy: {
    title: "official.privacy.metaTitle",
    description: "official.privacy.metaDescription",
    path: "/privacy",
  },
  support: {
    title: "official.support.metaTitle",
    description: "official.support.metaDescription",
    path: "/support",
  },
  terms: {
    title: "official.terms.metaTitle",
    description: "official.terms.metaDescription",
    path: "/terms",
  },
  notFound: {
    title: "official.notFound.title",
    description: "official.notFound.body",
    path: null,
  },
};

const openGraphLocales: Record<Locale, string> = {
  en: "en_US",
  "zh-Hant": "zh_TW",
  de: "de_DE",
};

export type SeoMetadata = {
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: string;
  openGraphLocale: string;
  imageAlt: string;
};

export function getSeoMetadata(
  locale: Locale,
  route: OfficialRoute,
): SeoMetadata {
  const keys = routeMetadataKeys[route];
  return {
    title: t(locale, keys.title),
    description: t(locale, keys.description),
    canonicalUrl: keys.path ? `${officialSiteOrigin}${keys.path}` : null,
    robots:
      route === "notFound"
        ? "noindex,nofollow"
        : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    openGraphLocale: openGraphLocales[locale],
    imageAlt: t(locale, "official.meta.imageAlt"),
  };
}

function setNamedMeta(name: string, content: string): void {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setPropertyMeta(property: string, content: string): void {
  let element = document.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(url: string | null): void {
  const existing = document.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!url) {
    existing?.remove();
    return;
  }
  const element = existing ?? document.createElement("link");
  element.rel = "canonical";
  element.href = url;
  if (!existing) document.head.appendChild(element);
}

export function applyPageMetadata(locale: Locale, route: OfficialRoute): void {
  const metadata = getSeoMetadata(locale, route);
  document.title = metadata.title;
  setCanonical(metadata.canonicalUrl);
  setNamedMeta("description", metadata.description);
  setNamedMeta("robots", metadata.robots);
  setNamedMeta("googlebot", metadata.robots);
  setNamedMeta("twitter:card", "summary_large_image");
  setNamedMeta("twitter:title", metadata.title);
  setNamedMeta("twitter:description", metadata.description);
  setNamedMeta("twitter:image", officialSocialImage);
  setNamedMeta("twitter:image:alt", metadata.imageAlt);
  setPropertyMeta("og:title", metadata.title);
  setPropertyMeta("og:description", metadata.description);
  setPropertyMeta("og:type", "website");
  setPropertyMeta("og:site_name", "Collectie");
  setPropertyMeta("og:locale", metadata.openGraphLocale);
  setPropertyMeta("og:url", metadata.canonicalUrl ?? officialSiteOrigin);
  setPropertyMeta("og:image", officialSocialImage);
  setPropertyMeta("og:image:secure_url", officialSocialImage);
  setPropertyMeta("og:image:type", "image/png");
  setPropertyMeta("og:image:width", "1200");
  setPropertyMeta("og:image:height", "630");
  setPropertyMeta("og:image:alt", metadata.imageAlt);
}
